import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { tryJsonParseSearchParam } from '../../../hooks/useDataGridState';
import { SearchModel } from '../types';

const SEARCH_MODEL_DEFAULT: SearchModel = {
  filter: 'eq(is_root, true)',
  searchFilter: 'eq(is_root, true)',
};

function getInitialSearchModel(searchParams: URLSearchParams): SearchModel {
  const params = tryJsonParseSearchParam(
    searchParams,
    'searchModel',
    SEARCH_MODEL_DEFAULT as SearchModel & { query?: string }
  );

  if (params.query) {
    return { filter: params.query };
  }

  return params;
}

export function useSearchModel() {
  const [searchParams] = useSearchParams();

  const [searchModel, _setSearchModel] = useState(
    getInitialSearchModel(searchParams)
  );

  // reference used inside updateSearchParams
  // to avoid stale closure capturing old searchModel
  // this can happen when searchModel is being changed via URL
  // outside of this hook
  const searchModelRef = useRef(searchModel);
  const setSearchModel: typeof _setSearchModel = useCallback(
    (newModel) => {
      if (typeof newModel === 'function') {
        _setSearchModel((prevModel) => {
          const next = newModel(prevModel);
          prevStrValue.current = JSON.stringify(next);
          searchModelRef.current = next;
          return next;
        });
      } else {
        prevStrValue.current = JSON.stringify(newModel);
        searchModelRef.current = newModel;
        _setSearchModel(newModel);
      }
    },
    [_setSearchModel]
  );

  // listen to changes in searchParams
  // and update accordingly
  const currStrValue = JSON.stringify(getInitialSearchModel(searchParams));
  const prevStrValue = useRef<typeof currStrValue>(currStrValue);
  useEffect(() => {
    if (currStrValue !== prevStrValue.current) {
      const next = JSON.parse(currStrValue);
      searchModelRef.current = next;
      _setSearchModel(next);
    }
    prevStrValue.current = currStrValue;
  }, [currStrValue]);

  const updateSearchParams = useCallback((params: URLSearchParams) => {
    const stringifiedSearchModel = JSON.stringify({
      ...searchModelRef.current,
      state: undefined,
    });
    if (stringifiedSearchModel !== JSON.stringify(SEARCH_MODEL_DEFAULT)) {
      params.set('searchModel', stringifiedSearchModel);
    } else {
      params.delete('searchModel');
    }
  }, []);

  return {
    searchModel,
    setSearchModel,
    updateSearchParams,
  };
}
